import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, Order } from "../api";
import { formatVND, formatTime, paymentLabel } from "../utils";
import { GlassPanel, Badge, SkeletonTable, ErrorState, EmptyState } from "../components";
import { useAutoRefresh } from "../hooks";

const TABS = [
  { value: "", label: "Tất cả" },
  { value: "CASH", label: "Tiền mặt" },
  { value: "CARD", label: "Thẻ" },
  { value: "OTHER", label: "Khác" },
];

const PRESETS = [
  { key: "today", label: "Hôm nay", days: 0 },
  { key: "7d", label: "7 ngày", days: 6 },
  { key: "30d", label: "30 ngày", days: 29 },
];

function todayVN(): string {
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}
function offsetDay(days: number): string {
  const d = new Date(Date.now() + 7 * 3600 * 1000 - days * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<string>("");
  const [from, setFrom] = useState(offsetDay(6));
  const [to, setTo] = useState(todayVN());
  const [preset, setPreset] = useState("7d");

  const loadOrders = (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    return api
      .orders({
        limit: 200,
        from,
        to,
        payment_type: paymentFilter || undefined,
      })
      .then((r) => setOrders(r.orders))
      .catch((e) => {
        if (!silent) setError(e.message);
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadOrders(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentFilter, from, to]);

  // Auto-refresh every 30s (silent — keep old data, no skeleton blink)
  useAutoRefresh(() => loadOrders(true), 30000);

  function applyPreset(p: typeof PRESETS[number]) {
    setPreset(p.key);
    setTo(todayVN());
    setFrom(offsetDay(p.days));
  }

  const grouped = useMemo(() => groupByDay(orders), [orders]);

  return (
    <>
      <div className="daterange">
        <div className="field">
          <label>Từ ngày</label>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setPreset("");
              setFrom(e.target.value);
            }}
          />
        </div>
        <div className="field">
          <label>Đến ngày</label>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setPreset("");
              setTo(e.target.value);
            }}
          />
        </div>
        <div className="preset">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              className={preset === p.key ? "active" : ""}
              onClick={() => applyPreset(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <GlassPanel
        title="Đơn hàng"
        badge={`${orders.length} đơn`}
        badgeVariant="info"
      >
        <div className="tabs" style={{ marginBottom: 16 }}>
          {TABS.map((t) => (
            <div
              key={t.value}
              className={`tab-item ${paymentFilter === t.value ? "active" : ""}`}
              onClick={() => setPaymentFilter(t.value)}
            >
              {t.label}
            </div>
          ))}
        </div>

        {loading ? (
          <SkeletonTable rows={8} cols={5} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => loadOrders(false)} />
        ) : orders.length === 0 ? (
          <EmptyState icon="shopping_cart" title="Không có đơn nào" description="Không có đơn nào trong khoảng thời gian này." />
        ) : (
          <div>
            {grouped.map(([day, list, total]) => (
              <div className="day-group" key={day}>
                <div className="day-group-head">
                  <span>{formatDayLabel(day)}</span>
                  <span>
                    <span className="day-total">{formatVND(total)}</span>{" "}
                    <span style={{ color: "var(--text-tertiary)" }}>· {list.length} đơn</span>
                  </span>
                </div>
                <div className="list">
                  {list.map((o) => (
                    <Link
                      to={`/orders/${o.receipt_number}`}
                      className="list-item"
                      key={o.receipt_number}
                    >
                      <div>
                        <div className="name">#{o.receipt_number}</div>
                        <div className="meta">
                          {formatTime(o.receipt_date)} ·{" "}
                          <Badge variant="ghost">{paymentLabel(o.payment_type)}</Badge>
                        </div>
                      </div>
                      <div className="right">
                        <div className="amount">{formatVND(o.total_money)}</div>
                        {o.total_discount > 0 && (
                          <div className="meta">−{formatVND(o.total_discount)}</div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
    </>
  );
}

function groupByDay(orders: Order[]): [string, Order[], number][] {
  const m = new Map<string, Order[]>();
  for (const o of orders) {
    const d = new Date(o.receipt_date);
    const vn = new Date(d.getTime() + 7 * 3600 * 1000);
    const key = vn.toISOString().slice(0, 10);
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(o);
  }
  const out: [string, Order[], number][] = [];
  for (const [day, list] of m) {
    list.sort((a, b) => b.receipt_date.localeCompare(a.receipt_date));
    const total = list.reduce((s, o) => s + (o.total_money || 0), 0);
    out.push([day, list, total]);
  }
  out.sort(([a], [b]) => b.localeCompare(a));
  return out;
}

function formatDayLabel(day: string): string {
  const today = todayVN();
  const yest = offsetDay(1);
  if (day === today) return `Hôm nay · ${day}`;
  if (day === yest) return `Hôm qua · ${day}`;
  return day;
}
